import { DefaultButton, Dialog, DialogType, getTheme, PrimaryButton, Stack, Text } from '@fluentui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';
import { berlinDateString } from 'kits/berlinDateKit';
import { isInArea } from 'features/InactivityRules/useInactivityRules';
import { usePresencePrompt } from 'features/InactivityRules/presencePromptState';
import {
    AWAY_SELECTED_MANUALLY_KEY,
    clearIdleChainStage,
    clearOooSelectedManually,
    readIdleChainStage,
    removeManualFlag
} from 'features/InactivityRules/inactivityStorage';
import { formatDateToLocale } from 'features/Rooms/RoomList/helpers/oooPhrase';
import {
    acknowledgeOooStatusReminder,
    isOooReturnDatePast,
    OOO_STATUS_REMINDER_ACK_KEY,
    readOooStatusReminderAck,
    shouldShowOooStatusReminder
} from 'features/Rooms/RoomList/helpers/oooStatusReminder';
import { useAtWorkCustomPhrase, useAtWorkLocation, useAwaySinceTimestamp, useOutOfOfficeReturnDate, useUserStatus } from 'shared-state/identity/hooks';
import { useCurrentAreaId } from 'shared-state/location/hook';

// Lets area entry settle first (e.g. Main.state.tsx resetting the status to available after an idle-chain lobby move)
const ENTRY_CHECK_DELAY_MS = 1000;

/**
 * Asks a user who enters an area while still out of office (e.g. after signing back in following the lobby
 * lockout, or after the return date passed) whether to change the status. Only checked on area entry,
 * so choosing out of office from the status menu never opens it.
 * Keep → not asked again today. Set to At work → available + office.
 */
const OooStatusReminderModal: React.FC = () => {
    const theme = getTheme();
    const [status, setStatus] = useUserStatus();
    const [returnDate] = useOutOfOfficeReturnDate();
    const [, setAtWorkLocation] = useAtWorkLocation();
    const [, setAtWorkCustomPhrase] = useAtWorkCustomPhrase();
    const [, setAwaySinceTimestamp] = useAwaySinceTimestamp();
    const [currentAreaId] = useCurrentAreaId();
    const [presencePrompt] = usePresencePrompt();
    const [isOpen, setIsOpen] = useState(false);

    // Latest values for the delayed entry check
    const latest = useRef({ status, currentAreaId, presencePrompt });
    latest.current = { status, currentAreaId, presencePrompt };

    useEffect(() => {
        if (!isInArea(currentAreaId)) return;
        const id = setTimeout(() => {
            const shouldShow = shouldShowOooStatusReminder({
                status: latest.current.status,
                today: berlinDateString(),
                inArea: isInArea(latest.current.currentAreaId),
                idleChainStage: readIdleChainStage(),
                presencePromptOpen: latest.current.presencePrompt !== null,
                acknowledgedOn: readOooStatusReminderAck()
            });
            if (shouldShow) setIsOpen(true);
        }, ENTRY_CHECK_DELAY_MS);
        return () => clearTimeout(id);
    }, [currentAreaId]);

    useEffect(() => {
        // an answer in another tab closes the reminder here too
        const onStorage = (event: StorageEvent) => {
            if ((event.key === OOO_STATUS_REMINDER_ACK_KEY || event.key === null) && readOooStatusReminderAck() === berlinDateString()) setIsOpen(false);
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const handleKeep = useCallback(() => {
        acknowledgeOooStatusReminder();
        setIsOpen(false);
    }, []);

    const handleSetAtWork = useCallback(() => {
        setIsOpen(false);
        setStatus('available');
        setAtWorkLocation('office');
        setAtWorkCustomPhrase(undefined);
        removeManualFlag(AWAY_SELECTED_MANUALLY_KEY);
        clearOooSelectedManually();
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    }, [setStatus, setAtWorkLocation, setAtWorkCustomPhrase, setAwaySinceTimestamp]);

    // the status may have changed meanwhile (e.g. in another tab)
    if (!isOpen || status !== 'out-of-office' || !isInArea(currentAreaId)) return null;

    const isExpired = isOooReturnDatePast(returnDate, berlinDateString());
    const text =
        isExpired && returnDate
            ? IvicosStrings.oooReminderExpiredText.replace('{date}', formatDateToLocale(new Date(returnDate)))
            : IvicosStrings.oooReminderText;

    return (
        <Dialog
            hidden={false}
            onDismiss={handleKeep}
            maxWidth="450px"
            minWidth="450px"
            dialogContentProps={{
                type: DialogType.close,
                title: isExpired ? IvicosStrings.oooReminderExpiredTitle : IvicosStrings.oooReminderTitle,
                closeButtonAriaLabel: IvicosStrings.awayExtensionModalCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 40 }} aria-label="ooo-status-reminder-modal">
                <Text variant="medium" style={{ color: theme.palette.black }}>
                    {text}
                </Text>
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                    <DefaultButton aria-label="ooo-status-reminder-keep" text={IvicosStrings.oooReminderKeep} onClick={handleKeep} />
                    <PrimaryButton aria-label="ooo-status-reminder-set-at-work" text={IvicosStrings.oooReminderSetAtWork} onClick={handleSetAtWork} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default OooStatusReminderModal;
